import React, { useContext, useEffect, useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Login from './views/login/Login';
import DetallesUsuario from './views/details/DetallesUsuario';
import SuccessPayment from './views/payments/SuccessPayment';
import FailedPayment from './views/payments/FailedPayment';
import Checkout from './views/payments/Checkout';
import CambioTarjeta from './views/payments/CambioTarjeta';
import Loader from './layouts/loader/Loader';
import { AuthContext } from './context/authContext';
import loginApiService from './services/http/loginApiService';
import useAuth from './hooks/useAuth';
import { removeItem, STORAGE_KEYS } from './services/storage';
import config from './config';

const PrivateRoute = ({ element, isVerified, isCheckingAuth }) => {
  if (isCheckingAuth) return <Loader />;
  return isVerified ? element : <Navigate to="/login" />;
};

PrivateRoute.propTypes = {
  element: PropTypes.node.isRequired,
  isVerified: PropTypes.bool.isRequired,
  isCheckingAuth: PropTypes.bool.isRequired,
};

const App = () => {
  const { user, logoutGoogle } = useContext(AuthContext);
  const { getUser } = useAuth();
  const userSession = getUser();
  const [isVerified, setIsVerified] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(!!userSession);

  useEffect(() => {
    // production → azul (default), test → rojo, sin valor (localhost) → verde
    const env =
      config.REACT_APP_DEPLOYMENT === 'production'
        ? 'production'
        : config.REACT_APP_DEPLOYMENT === 'test'
        ? 'test'
        : 'development';
    document.documentElement.setAttribute('data-env', env);
  }, []);

  useEffect(() => {
    const verificarTokenDeUsuario = async () => {
      try {
        await loginApiService.verifyToken();
        setIsVerified(true);
      } catch (error) {
        setIsVerified(false);
        logoutGoogle();
        removeItem(STORAGE_KEYS.USER);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    if (userSession) {
      verificarTokenDeUsuario();
    } else {
      setIsCheckingAuth(false);
    }
  }, [userSession, logoutGoogle]);

  return (
    <Routes>
      {/* Rutas de retorno desde Redsys accesibles para todos */}
      <Route path="/successPayment" element={<SuccessPayment />} />
      <Route path="/failedPayment" element={<FailedPayment />} />
      {/* Rutas públicas de pago (sin login) por sede.
          /pagar y /checkout son alias — los emails del backend usan /pagar (ver doc API),
          el portal acepta ambas para tolerar URLs antiguas y nuevas. */}
      <Route
        path="/cedesca/checkout"
        element={<Checkout sede="cedesca" defaultUid="21345675435675asfdsfs2q34532455423" />}
      />
      <Route
        path="/cedesca/pagar"
        element={<Checkout sede="cedesca" defaultUid="21345675435675asfdsfs2q34532455423" />}
      />
      <Route
        path="/cntec/checkout"
        element={<Checkout sede="cntec" defaultUid="98762345fghjklqwerty8765432qwerty1z" />}
      />
      <Route
        path="/cntec/pagar"
        element={<Checkout sede="cntec" defaultUid="98762345fghjklqwerty8765432qwerty1z" />}
      />
      {/* Rutas públicas de cambio de tarjeta (sin login) por sede.
          El enlace lo manda el backend por email con ?uid=... */}
      <Route path="/cedesca/cambio-tarjeta" element={<CambioTarjeta sede="cedesca" />} />
      <Route path="/cntec/cambio-tarjeta" element={<CambioTarjeta sede="cntec" />} />
      {/* Ruta de login */}
      <Route path="/login" element={user ? <Navigate to="/detalles" /> : <Login />} />

      {/* Ruta privada que redirige a login si no está verificado */}
      <Route
        path="/detalles"
        element={
          <PrivateRoute
            isVerified={isVerified}
            isCheckingAuth={isCheckingAuth}
            element={<DetallesUsuario />}
          />
        }
      />

      {/* Ruta por defecto que redirige dependiendo del estado de autenticación */}
      <Route path="*" element={<Navigate to={user ? '/detalles' : '/login'} />} />
    </Routes>
  );
};

export default App;
